import React, { useState } from "react";
import { useQuery } from "convex/react";
import { api } from "../../convex/_generated/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import {
  Building,
  Home,
  TreePine,
  Mountain,
  DollarSign,
  Calendar,
  MapPin,
  Bed,
  Bath,
  Square,
  User,
  Users,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { AgentPropertiesDialog } from "@/components/agent-properties-dialog";

interface PropertyTypeStats {
  type: string;
  icon: React.ReactNode;
  color: string;
  count: number;
  percentage: number;
  avgPrice?: string;
  avgBedrooms?: number;
  avgBathrooms?: number;
  avgBuildSize?: number;
  avgLandSize?: number;
  description: string;
}

export function Analytics() {
  const propertyTypeStats = useQuery(
    (api as any).properties.getPropertyTypeStats
  );
  const agentStats = useQuery(
    (api as any).properties.getPropertiesByListingAgent
  );
  const areaStats = useQuery(
    (api as any).properties.getPropertiesByAreaHierarchy
  );
  const agentAreaStats = useQuery(
    (api as any).properties.getPropertiesByAgentAndArea
  );

  return (
    <div className="w-full max-w-none space-y-6 overflow-y-auto p-6">
      <div>
        <h3 className="text-2xl font-bold tracking-tight">
          Property Analytics
        </h3>
        <p className="text-muted-foreground">
          Detailed insights and analytics for your property portfolio.
        </p>
      </div>

      {/* Property Type Breakdown */}
      <PropertyTypeBreakdown stats={propertyTypeStats} />

      {/* Properties by Listing Agent */}
      <PropertiesByAgent stats={agentStats} />

      {/* Properties by Area Hierarchy */}
      <PropertiesByArea stats={areaStats} />

      {/* Properties by Agent and Area */}
      <PropertiesByAgentAndArea stats={agentAreaStats} />
    </div>
  );
}

function PropertyTypeBreakdown({ stats }: { stats: any }) {
  const [isExpanded, setIsExpanded] = useState(false);

  const propertyTypeStats = React.useMemo((): PropertyTypeStats[] => {
    if (!stats) return [];

    const typeIconMap = {
      Rentals: <Calendar className="h-5 w-5" />,
      "Freehold Villas": <Building className="h-5 w-5" />,
      "Freehold Properties": <Home className="h-5 w-5" />,
      "Leasehold Villas": <TreePine className="h-5 w-5" />,
      "Freehold Land": <Mountain className="h-5 w-5" />,
      "Leasehold Land": <MapPin className="h-5 w-5" />,
    };

    const typeColorMap = {
      Rentals: "bg-blue-500",
      "Freehold Villas": "bg-purple-500",
      "Freehold Properties": "bg-indigo-500",
      "Leasehold Villas": "bg-rose-500",
      "Freehold Land": "bg-emerald-500",
      "Leasehold Land": "bg-amber-500",
    };

    const typeDescriptionMap = {
      Rentals: "Monthly and yearly rental properties with full facilities",
      "Freehold Villas":
        "Luxury villas with permanent ownership and complete facilities",
      "Freehold Properties":
        "Residential properties with freehold title and modern amenities",
      "Leasehold Villas":
        "Premium villas with long-term lease agreements and facilities",
      "Freehold Land": "Investment land plots with permanent ownership rights",
      "Leasehold Land": "Development land with leasehold tenure agreements",
    };

    return stats.map((stat: any) => ({
      type: stat.type,
      icon: typeIconMap[stat.type as keyof typeof typeIconMap] || (
        <Home className="h-5 w-5" />
      ),
      color:
        typeColorMap[stat.type as keyof typeof typeColorMap] || "bg-gray-500",
      count: stat.count,
      percentage: stat.percentage,
      avgPrice: stat.avgPrice || undefined,
      avgBedrooms: stat.avgBedrooms || undefined,
      avgBathrooms: stat.avgBathrooms || undefined,
      description:
        typeDescriptionMap[stat.type as keyof typeof typeDescriptionMap] || "",
    }));
  }, [stats]);

  const totalProperties = propertyTypeStats.reduce(
    (sum, type) => sum + type.count,
    0
  );

  if (!stats) {
    return (
      <Card className="animate-pulse">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Building className="h-5 w-5" />
              Property Type Distribution
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
              className="flex items-center gap-1"
            >
              {isExpanded ? (
                <>
                  <ChevronDown className="h-4 w-4" />
                  Collapse
                </>
              ) : (
                <>
                  <ChevronRight className="h-4 w-4" />
                  Expand
                </>
              )}
            </Button>
          </CardTitle>
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
        </CardHeader>
        {isExpanded && (
          <CardContent>
            <div className="space-y-4">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-16 bg-gray-200 rounded"></div>
              ))}
            </div>
          </CardContent>
        )}
      </Card>
    );
  }

  if (propertyTypeStats.length === 0 || totalProperties === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Building className="h-5 w-5" />
              Property Type Distribution
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
              className="flex items-center gap-1"
            >
              {isExpanded ? (
                <>
                  <ChevronDown className="h-4 w-4" />
                  Collapse
                </>
              ) : (
                <>
                  <ChevronRight className="h-4 w-4" />
                  Expand
                </>
              )}
            </Button>
          </CardTitle>
        </CardHeader>
        {isExpanded && (
          <CardContent>
            <div className="text-center py-8">
              <Building className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium text-muted-foreground mb-2">
                No Properties Found
              </h3>
              <p className="text-sm text-muted-foreground">
                Start adding properties to see your portfolio breakdown.
              </p>
            </div>
          </CardContent>
        )}
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* overview card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Building className="h-5 w-5" />
              Property Type Distribution
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
              className="flex items-center gap-1"
            >
              {isExpanded ? (
                <>
                  <ChevronDown className="h-4 w-4" />
                  Collapse
                </>
              ) : (
                <>
                  <ChevronRight className="h-4 w-4" />
                  Expand
                </>
              )}
            </Button>
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Breakdown of {totalProperties} properties across different
            categories
          </p>
        </CardHeader>
        {!isExpanded && (
          <CardContent className="pt-0">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                Click expand to view detailed breakdown
              </span>
              <span className="font-medium">
                {propertyTypeStats.length} property types
              </span>
            </div>
          </CardContent>
        )}
        {isExpanded && (
          <CardContent>
            <div className="space-y-6">
              {propertyTypeStats.map((type, index) => (
                <div key={type.type} className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div
                        className={`p-2 rounded-lg ${type.color} text-white flex items-center justify-center`}
                      >
                        {type.icon}
                      </div>
                      <div>
                        <h4 className="font-medium">{type.type}</h4>
                        <p className="text-sm text-muted-foreground">
                          {type.description}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center gap-2">
                        <span className="text-2xl font-bold">{type.count}</span>
                        <Badge variant="secondary">{type.percentage}%</Badge>
                      </div>
                    </div>
                  </div>

                  <Progress value={type.percentage} className="h-2" />

                  {/* property type details - always show exactly 4 metrics */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 ml-14 p-4 bg-muted/30 rounded-lg">
                    {/* Metric 1: Always show price if available */}
                    {type.avgPrice ? (
                      <div className="flex items-center gap-2">
                        <DollarSign className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <p className="text-xs text-muted-foreground">
                            {type.type.includes("Rental")
                              ? "Avg Annual Price"
                              : "Avg Price"}
                          </p>
                          <p className="text-sm font-medium">{type.avgPrice}</p>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <Square className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <p className="text-xs text-muted-foreground">
                            Total Properties
                          </p>
                          <p className="text-sm font-medium">{type.count}</p>
                        </div>
                      </div>
                    )}

                    {/* Metric 2: Bedrooms for residential or portfolio share for land */}
                    {type.avgBedrooms !== undefined && type.avgBedrooms > 0 ? (
                      <div className="flex items-center gap-2">
                        <Bed className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <p className="text-xs text-muted-foreground">
                            Avg Bedrooms
                          </p>
                          <p className="text-sm font-medium">
                            {type.avgBedrooms}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <p className="text-xs text-muted-foreground">
                            Portfolio Share
                          </p>
                          <p className="text-sm font-medium">
                            {type.percentage}%
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Metric 3: Bathrooms for residential or land size for land types */}
                    {type.avgBathrooms !== undefined &&
                    type.avgBathrooms > 0 ? (
                      <div className="flex items-center gap-2">
                        <Bath className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <p className="text-xs text-muted-foreground">
                            Avg Bathrooms
                          </p>
                          <p className="text-sm font-medium">
                            {type.avgBathrooms}
                          </p>
                        </div>
                      </div>
                    ) : type.avgLandSize ? (
                      <div className="flex items-center gap-2">
                        <Mountain className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <p className="text-xs text-muted-foreground">
                            Avg Land Size
                          </p>
                          <p className="text-sm font-medium">
                            {type.avgLandSize >= 10000
                              ? `${(type.avgLandSize / 10000).toFixed(1)} ha`
                              : `${type.avgLandSize.toLocaleString()} m²`}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <Building className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <p className="text-xs text-muted-foreground">
                            Ownership Type
                          </p>
                          <p className="text-sm font-medium">
                            {type.type.includes("Freehold")
                              ? "Freehold"
                              : "Leasehold"}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Metric 4: Build size for residential, ownership type for land, or total properties */}
                    {type.avgBuildSize ? (
                      <div className="flex items-center gap-2">
                        <Square className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <p className="text-xs text-muted-foreground">
                            Avg Build Size
                          </p>
                          <p className="text-sm font-medium">
                            {type.avgBuildSize.toLocaleString()} m²
                          </p>
                        </div>
                      </div>
                    ) : type.type.includes("Land") ? (
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <p className="text-xs text-muted-foreground">
                            Tenure Type
                          </p>
                          <p className="text-sm font-medium">
                            {type.type.includes("Freehold")
                              ? "Permanent"
                              : "Leased"}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <Square className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <p className="text-xs text-muted-foreground">
                            Total Properties
                          </p>
                          <p className="text-sm font-medium">{type.count}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        )}
      </Card>

      {/* summary statistics */}
      {isExpanded && (
        <div className="grid grid-cols-1 md:id-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Home className="h-8 w-8 text-blue-500" />
                <div>
                  <p className="text-sm text-muted-foreground">
                    Residential Properties
                  </p>
                  <p className="text-xl font-bold">
                    {propertyTypeStats
                      .filter((t) =>
                        [
                          "Rentals",
                          "Freehold Properties",
                          "Freehold Villas",
                          "Leasehold Villas",
                        ].includes(t.type)
                      )
                      .reduce((sum, t) => sum + t.count, 0)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {Math.round(
                      (propertyTypeStats
                        .filter((t) =>
                          [
                            "Rentals",
                            "Freehold Properties",
                            "Freehold Villas",
                            "Leasehold Villas",
                          ].includes(t.type)
                        )
                        .reduce((sum, t) => sum + t.count, 0) /
                        totalProperties) *
                        100
                    )}
                    % of portfolio
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Mountain className="h-8 w-8 text-emerald-500" />
                <div>
                  <p className="text-sm text-muted-foreground">
                    Land Properties
                  </p>
                  <p className="text-xl font-bold">
                    {propertyTypeStats
                      .filter((t) =>
                        ["Freehold Land", "Leasehold Land"].includes(t.type)
                      )
                      .reduce((sum, t) => sum + t.count, 0)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {Math.round(
                      (propertyTypeStats
                        .filter((t) =>
                          ["Freehold Land", "Leasehold Land"].includes(t.type)
                        )
                        .reduce((sum, t) => sum + t.count, 0) /
                        totalProperties) *
                        100
                    )}
                    % of portfolio
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Building className="h-8 w-8 text-green-500" />
                <div>
                  <p className="text-sm text-muted-foreground">Freehold</p>
                  <p className="text-xl font-bold">
                    {propertyTypeStats
                      .filter((t) =>
                        [
                          "Freehold Properties",
                          "Freehold Villas",
                          "Freehold Land",
                        ].includes(t.type)
                      )
                      .reduce((sum, t) => sum + t.count, 0)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {Math.round(
                      (propertyTypeStats
                        .filter((t) =>
                          [
                            "Freehold Properties",
                            "Freehold Villas",
                            "Freehold Land",
                          ].includes(t.type)
                        )
                        .reduce((sum, t) => sum + t.count, 0) /
                        totalProperties) *
                        100
                    )}
                    % ownership
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Calendar className="h-8 w-8 text-orange-500" />
                <div>
                  <p className="text-sm text-muted-foreground">Leasehold</p>
                  <p className="text-xl font-bold">
                    {propertyTypeStats
                      .filter((t) =>
                        ["Leasehold Villas", "Leasehold Land"].includes(t.type)
                      )
                      .reduce((sum, t) => sum + t.count, 0)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {Math.round(
                      (propertyTypeStats
                        .filter((t) =>
                          ["Leasehold Villas", "Leasehold Land"].includes(
                            t.type
                          )
                        )
                        .reduce((sum, t) => sum + t.count, 0) /
                        totalProperties) *
                        100
                    )}
                    % leased
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

function PropertiesByAgent({ stats }: { stats: any }) {
  const [selectedAgent, setSelectedAgent] = useState<{
    agentName: string;
    originalIds: string[];
  } | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  if (!stats) return <div>Loading...</div>;

  const handleAgentClick = (agent: any) => {
    setSelectedAgent({
      agentName: agent.agentName,
      originalIds: agent.originalIds || [],
    });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedAgent(null);
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Properties by Listing Agent
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Property listings grouped by the agent who created them. Click on an
            agent name to view their properties.
          </p>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-2">Agent Name</th>
                  <th className="text-left p-2">Email</th>
                  <th className="text-right p-2">Property Count</th>
                </tr>
              </thead>
              <tbody>
                {stats.map((agent: any) => (
                  <tr
                    key={agent.agentId}
                    className="border-b hover:bg-muted/50"
                  >
                    <td className="p-2">
                      <button
                        onClick={() => handleAgentClick(agent)}
                        className="text-left font-medium hover:text-primary hover:underline cursor-pointer active:scale-95 transition-all duration-150 ease-out"
                        title="Click to view properties"
                      >
                        {agent.agentName}
                      </button>
                    </td>
                    <td className="p-2 text-muted-foreground">
                      {agent.agentEmail}
                    </td>
                    <td className="p-2 text-right font-medium">
                      {agent.propertyCount}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {selectedAgent && (
        <AgentPropertiesDialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          agentName={selectedAgent.agentName}
          originalIds={selectedAgent.originalIds}
        />
      )}
    </>
  );
}

function PropertiesByArea({ stats }: { stats: any }) {
  if (!stats) return <div>Loading...</div>;

  const renderAreaRow = (area: any, depth: number = 0) => {
    const indent = depth * 24;
    return (
      <>
        <tr key={area.areaId} className="border-b hover:bg-muted/50">
          <td className="p-2" style={{ paddingLeft: `${indent + 8}px` }}>
            {depth > 0 && (
              <span className="text-muted-foreground mr-2">└─</span>
            )}
            {area.areaName}
          </td>
          <td className="p-2 text-center">
            <span className="text-xs px-2 py-1 rounded bg-muted">
              {area.level}
            </span>
          </td>
          <td className="p-2 text-right">{area.directCount}</td>
          <td className="p-2 text-right font-medium">{area.totalCount}</td>
        </tr>
        {area.children?.map((child: any) => renderAreaRow(child, depth + 1))}
      </>
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MapPin className="h-5 w-5" />
          Properties by Area
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Property distribution across areas and sub-areas
        </p>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b">
                <th className="text-left p-2">Area Name</th>
                <th className="text-center p-2">Level</th>
                <th className="text-right p-2">Direct Count</th>
                <th className="text-right p-2">Total Count</th>
              </tr>
            </thead>
            <tbody>{stats.map((area: any) => renderAreaRow(area, 0))}</tbody>
          </table>
        </div>
        <p className="text-xs text-muted-foreground mt-4">
          * Direct Count: Properties directly in this area. Total Count:
          Includes all sub-areas.
        </p>
      </CardContent>
    </Card>
  );
}

function PropertiesByAgentAndArea({ stats }: { stats: any }) {
  if (!stats) return <div>Loading...</div>;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-5 w-5" />
          Properties by Agent and Area
        </CardTitle>
      </CardHeader>
      <CardContent className="p-2 px-6">
        <div className="space-y-1.5">
          {stats.map((agent: any) => (
            <div
              key={agent.agentName}
              className="py-2 border-b last:border-b-0"
            >
              <div className="flex items-start gap-4">
                <div className="min-w-[120px] font-medium text-sm py-0.5">
                  {agent.agentName}
                </div>
                <div className="flex-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs">
                  {agent.areaBreakdown.map((area: any) => (
                    <span key={area.areaName} className="text-muted-foreground">
                      {area.areaName}:{" "}
                      <span className="font-medium text-foreground">
                        {area.count}
                      </span>
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
